import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { authApi, getApiErrorMessage, ledgerMirrorApi } from 'api';
import { useAuth } from 'contexts/AuthContext';

const SECRET_CLICKS = 5;
const SECRET_WINDOW_MS = 2500;

export function useLedgerSecretToggle() {
  const toast = useToast();
  const { user, setUser } = useAuth();
  const canToggle = !!(user?.isDirector && user?.ledgerMirrorEnabled);
  const activeLedger = user?.activeLedger || 'primary';
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  const loadStatus = useCallback(async () => {
    if (!canToggle) return;
    try {
      const data = await ledgerMirrorApi.status();
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, [canToggle]);

  useEffect(() => {
    if (modalOpen) {
      loadStatus();
    }
  }, [modalOpen, loadStatus]);

  const handleSecretClick = useCallback(() => {
    if (!canToggle) return;

    clickCountRef.current += 1;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }
    clickTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, SECRET_WINDOW_MS);

    if (clickCountRef.current >= SECRET_CLICKS) {
      clickCountRef.current = 0;
      setModalOpen(true);
    }
  }, [canToggle]);

  const applyLedger = useCallback(
    async (ledger) => {
      setSaving(true);
      try {
        const updated = await authApi.updateActiveLedger(ledger);
        if (setUser) {
          setUser(updated);
        }
        setModalOpen(false);
        window.location.reload();
      } catch (e) {
        toast({
          title: 'Не удалось переключить версию БД',
          description: getApiErrorMessage(e),
          status: 'error',
        });
      } finally {
        setSaving(false);
      }
    },
    [setUser, toast]
  );

  const modalBg = useColorModeValue('white', 'navy.800');
  const muted = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const counts = status?.counts || {};

  const modal = (
    <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} isCentered size="lg">
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>Версия базы данных</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color={muted} mb={4}>
            V1 — оригинал (источник правды). V2 — зеркало: амбулатория и доп. услуги сразу;
            стационар и операции — по 2–3 в сутки.
          </Text>
          <VStack align="stretch" spacing={2} mb={4}>
            <Button
              variant={activeLedger === 'primary' ? 'solid' : 'outline'}
              colorScheme="brand"
              onClick={() => applyLedger('primary')}
              isLoading={saving}
            >
              V1 · Оригинал
            </Button>
            <Button
              variant={activeLedger === 'mirror' ? 'solid' : 'outline'}
              colorScheme="purple"
              onClick={() => applyLedger('mirror')}
              isLoading={saving}
            >
              V2 · Зеркало
            </Button>
          </VStack>
          {status ? (
            <Box>
              <Text fontSize="sm" fontWeight="600" mb={2}>
                Очередь зеркала
              </Text>
              <SimpleGrid columns={4} spacing={2} mb={3}>
                <StatusChip label="pending" value={counts.pending ?? 0} />
                <StatusChip label="synced" value={counts.synced ?? 0} />
                <StatusChip label="ops today" value={status.operationsSyncedToday ?? 0} />
                <StatusChip label="stat today" value={status.inpatientSyncedToday ?? 0} />
              </SimpleGrid>
              <Text fontSize="xs" color={muted}>
                Health: {status.health?.message || '—'} · ops/day {status.operationsPerDay} ·
                inpatient/day {status.inpatientPerDay}
              </Text>
            </Box>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>
            Закрыть
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  return { handleSecretClick, modal, canToggle, activeLedger };
}

function StatusChip({ label, value }) {
  return (
    <Box borderWidth="1px" borderRadius="md" px={2} py={1} textAlign="center">
      <Text fontSize="xs" opacity={0.7}>
        {label}
      </Text>
      <Text fontWeight="700">{value}</Text>
    </Box>
  );
}

/** Секретный триггер на бейдже версии: 5 кликов у директора. */
export function LedgerVersionBadge() {
  const { canToggle, handleSecretClick, modal, activeLedger } = useLedgerSecretToggle();
  const { user } = useAuth();

  if (!user?.isDirector || !user?.ledgerMirrorEnabled) {
    return null;
  }

  const isMirror = activeLedger === 'mirror';

  return (
    <Box as="span" display="inline-flex" alignItems="center" ml={2}>
      <Badge
        colorScheme={isMirror ? 'purple' : 'green'}
        variant="subtle"
        cursor={canToggle ? 'pointer' : 'default'}
        userSelect="none"
        onClick={canToggle ? handleSecretClick : undefined}
        title={canToggle ? 'Секретное переключение версии БД' : undefined}
      >
        {isMirror ? 'V2 зеркало' : 'V1 оригинал'}
      </Badge>
      {modal}
    </Box>
  );
}
